import ControlSlider from "./control-slider";
import { CONTROL_SLIDER_MARKER_PCTS } from "./slider-math";

// Normal: the slider runs 1 → 1.4 and maps to slowControl 1 → 0.6 (slower).
// With Advanced → Fast Control on, the same travel maps to 1 → 1.4, so the
// control speeds the video up instead. Either way the far end of the slider
// is "more effect", so the track and markers stay put.
// A `title` attribute never surfaced here, so the hint is a real element. It
// hangs below the badge: the title sits at the top of the scrolling controls,
// where there is nothing above to hang into.
function TitleBadge({ label, tip, className = "", children }) {
  return (
    <span
      className={`content__title-lock${className ? ` ${className}` : ""}`}
      tabIndex={0}
      role="note"
    >
      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        {children}
      </svg>
      <span className="content__title-lock-label">{label}</span>
      <span className="content__title-lock-tip">{tip}</span>
    </span>
  );
}

export default function SlowControlSection({
  slowControl,
  speedUpMode = false,
  pitchLocked = false,
  onSlowControlChange,
}) {
  const stepLabel = slowControl === 1 ? "None" : String(slowControl);
  const sliderValue = speedUpMode ? slowControl : 2 - slowControl;
  const pitchLockTitle = speedUpMode
    ? "Pitch Lock is on. The original pitch is kept while speeding up"
    : "Pitch Lock is on. The original pitch is kept while slowing down";

  return (
    <div className="content__item content__item--slow">
      <div className="content__title">
        {/* Fast Control keeps the title: the control is still Slow Control,
            turned around, and the badge beside Pitch Lock says so. */}
        Slow Control

        {/* Pitch Lock lives in Advanced, out of sight of the control it
            changes, so the control says so itself. The padlock is named:
            on its own it is the same glyph Pro-locked features wear, and a
            bare lock beside Slow Control would read as "you cannot use this". */}
        {pitchLocked && (
          <TitleBadge label="Pitch Lock" tip={pitchLockTitle}>
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </TitleBadge>
        )}

        {/* Fast Control also lives in Advanced and flips which way the slider
            goes, so it gets the same badge, to the right of Pitch Lock. */}
        {speedUpMode && (
          <TitleBadge
            label="Fast Mode"
            tip="Fast Control is on. This control speeds the video up instead of slowing it down"
            className="content__title-lock--fast"
          >
            <path d="M3 5.5a1 1 0 011.6-.8l5.4 4.5a1 1 0 010 1.6l-5.4 4.5A1 1 0 013 14.5v-9zm7.5 0a1 1 0 011.6-.8l5.4 4.5a1 1 0 010 1.6l-5.4 4.5a1 1 0 01-1.6-.8v-9z" />
          </TitleBadge>
        )}
      </div>
      <ControlSlider
        label={stepLabel}
        markerPcts={CONTROL_SLIDER_MARKER_PCTS}
        min="1"
        max="1.4"
        step="0.01"
        value={sliderValue}
        ghostEnabled
        onChange={(event) => {
          const raw = parseFloat(event.currentTarget.value);
          const next = speedUpMode ? raw : 2 - raw;
          onSlowControlChange(parseFloat(next.toFixed(2)));
        }}
      />
    </div>
  );
}
